import _, { groupBy, map, sortBy } from 'lodash';
import moment from 'moment';
import {
  EMPLOYEE_DRIVER,
  MINIMUM_ALLOWABLE_MANUFACTURING_YEAR,
  NEW_OWNER_YET_TO_CHANGE_AT_DVLA,
  NEW_OWNER_YET_TO_CHANGE_AT_FRSC,
  OTHER_AUTHORISED_KEEPER_OR_USER,
  POLICY_ISSUANCE_MODES_WITH_DESCRIPTION,
  SERVER_DATE_FORMAT,
  VEHICLE_BODY_TYPE_MAP,
  VEHICLE_PROTECTION_TYPES,
  VEHICLE_USAGES,
  WORK_AND_PAY_DRIVER
} from '../resources/constants';
import {
  ACCESS_RIGHTS,
  COVER_PERIODS,
  DISCOUNT_RELATION_OPTIONS,
  ENVIRONMENTS,
  FUEL_SALES_STATUS,
  ID_TYPES_DTO,
  INSTALLMENT_TYPES,
  LEAD_SOURCE,
  PARTNER_TYPES,
  PAYMENT_CHANNELS,
  POLICY_DOCUMENT_TYPES,
  POLICY_REVIEW_STATES,
  PROTECTION_TYPES,
  COMPANY_TYPES,
  UPLOADABLE_DOCUMENT_TYPES,
  VEHICLE_BODY_TYPES,
  VERIFIED_VEHICLE_DOCUMENT_TYPES,
  POLICY_ISSUANCE_MODES,
  SELECTABLE_ACCESS_RIGHTS,
} from '../resources/enums';
import {
  IClaimFormData,
  IDocument,
  IMarketingFunnel,
  IPolicyFormData,
  IPolicyPaymentData,
  ISupportedVehicles,
  ITableFilterItem,
  IUpdateClaimDTO,
  IUpdatePolicyDTO,
  IVehicleProtectionType,
  IVehicleUsages
} from '../resources/interfaces';
import { idTypesDTO } from '../resources/types';
import { IGeneralMetrics } from '../models/generalMetrics/IGeneralMetrics';
import { ISelectDropdownItem } from '../components/form-control/SelectDropdown';
import * as Yup from 'yup';
import { HomePolicy } from '../models/homePolicy/HomePolicy';
import { Policy } from '../models/policy/Policy';
import { PaystackProps } from 'react-paystack/dist/types';
import SupportedVehicle from '../models/SupportedVehicle';
import { ISelectMenuItemValue } from '../components/form-control/SelectMenu';
import { IChartItem } from '../views/partners/broker/commissions/components/CommissionDistributionCharts';
import { IPayment } from '../models/payment/IPayment';
import { FleetPolicy } from '../models/policy/FleetPolicy';
import { toast } from 'react-toastify';

export const getNameInitials = (name: any = '') => {
  let abbr = '';
  let str = name || '';
  str = str.split(' ');
  for (let i = 0; i < str.length; i++) {
    abbr += str[i].substr(0, 1);
  }
  if (abbr) {
    if (abbr[1]) {
      return abbr[0].toUpperCase() + abbr[1].toUpperCase();
    }
    return abbr[0].toUpperCase();
  }
};

const getColorInitial = (name: any = '') => {
  if (name) {
    let abbr = '';
    let str = name || '';
    str = str.split(' ');
    for (let i = 0; i < str.length; i++) {
      abbr += str[i].substr(0, 1);
    }

    return abbr[0].toUpperCase();
  }
};

export const getCurrentQuarter = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;

  if (currentMonth >= 1 && currentMonth <= 3) {
    return 1;
  } else if (currentMonth >= 4 && currentMonth <= 6) {
    return 2;
  } else if (currentMonth >= 7 && currentMonth <= 9) {
    return 3;
  } else {
    return 4;
  }
};

export function getYearsList(): number[] {
  const currentYear = new Date().getFullYear();
  const yearsArray = [];

  for (let year = currentYear; year >= 2022; year--) {
    yearsArray.push(year);
  }

  return yearsArray;
}

export const getNumberOfStringCharacters = (text: string, min: number, max: number) => {
  let slicedText = '';
  if (text) {
    slicedText = text.slice(min, max);
  }
  return `${slicedText}...`;
};

export const getPolicyDocumentTitleFromType = (type: UPLOADABLE_DOCUMENT_TYPES | VERIFIED_VEHICLE_DOCUMENT_TYPES | POLICY_DOCUMENT_TYPES | IDocument): string => {
  let title = '';
  switch (type) {
    case POLICY_DOCUMENT_TYPES.INSURANCE_STICKER:
      title = 'Sticker';
      break;

    case POLICY_DOCUMENT_TYPES.INSURANCE_CERTIFICATE:
      title = 'Insurance Certificate';
      break;

    case POLICY_DOCUMENT_TYPES.CONTRACT_SUMMARY:
      title = 'Contract Summary';
      break;

    case POLICY_DOCUMENT_TYPES.PAYMENT_AGREEMENT:
      title = 'Payment Agreement';
      break;
    case POLICY_DOCUMENT_TYPES.PAYMENT_LOAN_AGREEMENT:
      title = 'Signed Payment Agreement';
      break;
    case POLICY_DOCUMENT_TYPES.LOAN_AGREEMENT:
      title = 'Loan Agreement';
      break;

    case POLICY_DOCUMENT_TYPES.ROADWORTHY:
      title = 'Roadworthy';
      break;

    case POLICY_DOCUMENT_TYPES.ID_CARD:
      title = "Owner's ID";
      break;

    case POLICY_DOCUMENT_TYPES.PASSPORT_PHOTO:
      title = "Owner's Photograph";
      break;

    case POLICY_DOCUMENT_TYPES.VEHICLE_PHOTO_FRONT:
      title = "Vehicle's Front Photograph'";
      break;

    case POLICY_DOCUMENT_TYPES.VEHICLE_PHOTO_LEFT:
      title = "Vehicle's Left Photograph";
      break;

    case POLICY_DOCUMENT_TYPES.VEHICLE_PHOTO_BACK:
      title = "Vehicle's Back Photograph";
      break;

    case POLICY_DOCUMENT_TYPES.VEHICLE_PHOTO_RIGHT:
      title = "Vehicle's Right Photograph";
      break;

    case POLICY_DOCUMENT_TYPES.DRIVER_LICENSE_BACK:
      title = "Driver's License Back";
      break;

    case POLICY_DOCUMENT_TYPES.DRIVER_LICENSE_FRONT:
      title = "Driver's License Front";
      break;

    case POLICY_DOCUMENT_TYPES.RENEWAL_NOTICE:
      title = 'Renewal Notice';
      break;

    case POLICY_DOCUMENT_TYPES.POLICY_SCHEDULE:
      title = 'Policy Schedule';
      break;

    case POLICY_DOCUMENT_TYPES.POLICY:
      title = 'Policy Document';
      break;

    case VERIFIED_VEHICLE_DOCUMENT_TYPES.INSPECTION_REPORT:
      title = "Inspection Report";
      break;
  }

  return title;
};

export const getVerifiedVehicleDocumentTitleFromType = (type: UPLOADABLE_DOCUMENT_TYPES | VERIFIED_VEHICLE_DOCUMENT_TYPES | POLICY_DOCUMENT_TYPES): string => {
  let title = '';
  switch (type) {
    case VERIFIED_VEHICLE_DOCUMENT_TYPES.ID_CARD:
      title = "Owner's ID";
      break;

    case VERIFIED_VEHICLE_DOCUMENT_TYPES.PASSPORT_PHOTO:
      title = "Owner's Photograph";
      break;

    case VERIFIED_VEHICLE_DOCUMENT_TYPES.VEHICLE_PHOTO_FRONT:
      title = "Vehicle's Front Photograph'";
      break;

    case VERIFIED_VEHICLE_DOCUMENT_TYPES.VEHICLE_PHOTO_LEFT:
      title = "Vehicle's Left Photograph";
      break;

    case VERIFIED_VEHICLE_DOCUMENT_TYPES.VEHICLE_PHOTO_BACK:
      title = "Vehicle's Back Photograph";
      break;

    case VERIFIED_VEHICLE_DOCUMENT_TYPES.VEHICLE_PHOTO_RIGHT:
      title = "Vehicle's Right Photograph";
      break;

    case VERIFIED_VEHICLE_DOCUMENT_TYPES.DRIVER_LICENSE_BACK:
      title = "Driver's License Back";
      break;

    case VERIFIED_VEHICLE_DOCUMENT_TYPES.DRIVER_LICENSE_FRONT:
      title = "Driver's License Front";
      break;
    case VERIFIED_VEHICLE_DOCUMENT_TYPES.INSPECTION_REPORT:
      title = "Inspection Report";
      break;

      title = 'Policy Document';
      break;
  }

  return title;
};

export function getVehicleUsageFromKey(key: string): IVehicleUsages | null {
  if (!_.isEmpty(VEHICLE_USAGES)) {
    return VEHICLE_USAGES?.[key as keyof IVehicleUsages];
  }

  return null;
}

export function getVehicleProtectionFromKey(key: string | IVehicleProtectionType): IVehicleProtectionType | null {
  if (!_.isEmpty(VEHICLE_PROTECTION_TYPES)) {
    return VEHICLE_PROTECTION_TYPES?.[key as keyof IVehicleProtectionType] ?? { title: key };
  }

  return null;
}

export function getProtectTypeKeyFromString(type: string): PROTECTION_TYPES | null {
  switch (type?.trim()) {
    case 'Third Party':
      return PROTECTION_TYPES.THIRD_PARTY

    case 'Third Party (Fire/Theft)':
      return PROTECTION_TYPES.THIRD_PARTY_FIRE_THEFT

    case '85% Comprehensive':
      return PROTECTION_TYPES.COMPREHENSIVE_85

    case '90% Comprehensive':
      return PROTECTION_TYPES.COMPREHENSIVE_90

    case '100% Comprehensive':
      return PROTECTION_TYPES.COMPREHENSIVE_100

    case 'Smart Drive':
      return PROTECTION_TYPES.SMART_DRIVE

    default:
      return null
  }
}

export function getIdTypeFromValue(idType: idTypesDTO | string): string | undefined {
  switch (idType) {
    case 'PASSPORT':
      return 'Passport';
    case 'NATIONAL_ID':
      return 'National ID';
    case 'VOTER_ID':
      return "Voter's ID";
    case 'DRIVER_LICENSE':
      return "Driver's License";
    case 'SOCIAL_SECURITY':
      return 'Social Security';
    case 'TIN':
      return 'TIN';
    default:
      return undefined;
  }
}

export function getIdTypeFromString(idType: string) {
  switch (idType?.toLowerCase()) {
    case 'passport':
      return ID_TYPES_DTO.PASSPORT;
    case 'national id':
      return ID_TYPES_DTO.NATIONAL_ID;
    case "voter's id":
      return ID_TYPES_DTO.VOTER_ID;
    case "driver's license":
      return ID_TYPES_DTO.DRIVER_LICENSE;
    case 'tin':
      return ID_TYPES_DTO.TIN;
    default:
      return undefined;
  }
}

export const getPolicyReviewStatus = (status: POLICY_REVIEW_STATES | string | boolean): string => {
  switch (status) {
    case POLICY_REVIEW_STATES.APPROVED:
    case true:
      return 'Approved';

    case POLICY_REVIEW_STATES.CANCELLED:
      return 'Cancelled';

    case POLICY_REVIEW_STATES.ON_HOLD:
      return 'On Hold';

    case POLICY_REVIEW_STATES.IN_APPROVAL:
      return 'In Approval';

    case POLICY_REVIEW_STATES.DECLINED:
      return 'Declined';

    case POLICY_REVIEW_STATES.NEW:
    case false:
      return 'New';

    case POLICY_REVIEW_STATES.DRAFT:
      return 'Draft';

    case POLICY_REVIEW_STATES.IN_REVIEW:
      return 'In Review';

    case POLICY_REVIEW_STATES.ARCHIVED:
      return 'Archived';

    case POLICY_REVIEW_STATES.DUE_LATER:
      return 'Due Later';

    case POLICY_REVIEW_STATES.EXPIRED:
      return 'Expired';
  }

  return status;
};

export const getInstallmentTypeFromKey = (installment: INSTALLMENT_TYPES | string): string => {
  switch (installment) {
    case INSTALLMENT_TYPES.FULL_PAYMENT:
      return 'Annually';

    case INSTALLMENT_TYPES.TWELVE_MONTHS:
    case INSTALLMENT_TYPES.NINE_MONTHS:
    case INSTALLMENT_TYPES.SIX_MONTHS:
    case INSTALLMENT_TYPES.THREE_MONTHS:
    case INSTALLMENT_TYPES.MONTHLY:
      return 'Monthly';

    case INSTALLMENT_TYPES.TWELVE_WEEKS:
    case INSTALLMENT_TYPES.NINE_WEEKS:
    case INSTALLMENT_TYPES.TEN_WEEKS:
    case INSTALLMENT_TYPES.WEEKLY:
      return 'Weekly';

    case INSTALLMENT_TYPES.ONE_HUNDRED_AND_TWENTY_DAYS:
    case INSTALLMENT_TYPES.NINETY_DAYS:
    case INSTALLMENT_TYPES.SEVENTY_DAYS:
    case INSTALLMENT_TYPES.SIXTY_FIVE_DAYS:
    case INSTALLMENT_TYPES.SIXTY_DAYS:
    case INSTALLMENT_TYPES.FIFTY_FIVE_DAYS:
    case INSTALLMENT_TYPES.FORTY_DAYS:
    case INSTALLMENT_TYPES.THREE_DAYS_TEST:
    case INSTALLMENT_TYPES.DAILY:
      return 'Daily';

    default:
      return '';
  }
};

export const getInstallmentPeriodFromKey = (
  installment: INSTALLMENT_TYPES | string,
  policy?: Policy
): string => {
  switch (installment) {
    case INSTALLMENT_TYPES.FULL_PAYMENT:
      return 'FULL PAYMENT';

    case INSTALLMENT_TYPES.TWELVE_MONTHS:
      return 'TWELVE MONTHS';

    case INSTALLMENT_TYPES.NINE_MONTHS:
      return 'NINE MONTHS';

    case INSTALLMENT_TYPES.SIX_MONTHS:
      return 'SIX MONTHS';

    case INSTALLMENT_TYPES.THREE_MONTHS:
      return 'THREE MONTHS';

    case INSTALLMENT_TYPES.TWELVE_WEEKS:
      return 'TWELVE WEEKS';

    case INSTALLMENT_TYPES.NINE_WEEKS:
      return 'NINE WEEKS';

    case INSTALLMENT_TYPES.TEN_WEEKS:
      return 'TEN WEEKS';

    case INSTALLMENT_TYPES.ONE_HUNDRED_AND_TWENTY_DAYS:
      return 'ONE HUNDRED AND TWENTY DAYS';

    case INSTALLMENT_TYPES.NINETY_DAYS:
      return 'NINETY DAYS';

    case INSTALLMENT_TYPES.SEVENTY_DAYS:
      return 'SEVENTY DAYS';

    case INSTALLMENT_TYPES.SIXTY_FIVE_DAYS:
      return 'SIXTY FIVE DAYS';

    case INSTALLMENT_TYPES.SIXTY_DAYS:
      return 'SIXTY DAYS';

    case INSTALLMENT_TYPES.FORTY_DAYS:
      return 'FORTY DAYS';

    case INSTALLMENT_TYPES.THREE_DAYS_TEST:
      return 'THREE DAYS TEST';

    case INSTALLMENT_TYPES.MONTHLY:
      return 'MONTHLY';

    case INSTALLMENT_TYPES.WEEKLY:
      return 'WEEKLY';

    case INSTALLMENT_TYPES.DAILY:
      return 'DAILY';

    default:
      if (policy?.quote?.period) {
        return `${policy?.quote?.period} ${policy?.isDailyInstallment() ? 'Days' : policy?.isWeeklyInstallment() ? 'Weeks' : 'Months'
          }`;
      } else {
        return '';
      }
  }
};

export const getInstallmentPeriodPrefix = (installmentPeriod?: string) => {
  let prefix = '';

  if (installmentPeriod?.includes('_MONTHS')) prefix = 'Monthly';
  else if (installmentPeriod?.includes('_WEEKLY')) prefix = 'Weekly';
  else prefix = 'Daily';

  return prefix;
};

export const getNumOfInstallmentsFromInstallmentPeriod = (
  installment: INSTALLMENT_TYPES | string
): number => {
  switch (installment) {
    case INSTALLMENT_TYPES.FULL_PAYMENT:
      return 1;

    case INSTALLMENT_TYPES.TWELVE_MONTHS:
      return 11;

    case INSTALLMENT_TYPES.NINE_MONTHS:
      return 8;

    case INSTALLMENT_TYPES.SIX_MONTHS:
      return 5;

    case INSTALLMENT_TYPES.THREE_MONTHS:
      return 2;

    case INSTALLMENT_TYPES.TWELVE_WEEKS:
      return 11;

    case INSTALLMENT_TYPES.NINE_WEEKS:
      return 9;

    case INSTALLMENT_TYPES.TEN_WEEKS:
      return 10;

    case INSTALLMENT_TYPES.ONE_HUNDRED_AND_TWENTY_DAYS:
      return 125;

    case INSTALLMENT_TYPES.NINETY_DAYS:
      return 90;

    case INSTALLMENT_TYPES.SEVENTY_DAYS:
      return 70;

    case INSTALLMENT_TYPES.SIXTY_FIVE_DAYS:
      return 65;

    case INSTALLMENT_TYPES.SIXTY_DAYS:
      return 60;

    case INSTALLMENT_TYPES.FIFTY_FIVE_DAYS:
      return 55;

    case INSTALLMENT_TYPES.FORTY_DAYS:
      return 40;

    case INSTALLMENT_TYPES.THREE_DAYS_TEST:
      return 3;

    default:
      return 0;
  }
};

export const getFormattedLeadSource = (leadSource: LEAD_SOURCE | string) => {
  switch (leadSource) {
    case LEAD_SOURCE.ASEMPA_FM:
      return 'ASEMPA FM';
    case LEAD_SOURCE.CITIFM_ONLINE:
      return 'CITI FM ONLINE';
    case LEAD_SOURCE.CITI_FM:
      return 'CITI FM';
    case LEAD_SOURCE.CUSTOMER_REFERRAL:
      return 'CUSTOMER REFERRAL';
    case LEAD_SOURCE.FACEBOOK:
      return 'FACEBOOK';
    case LEAD_SOURCE.GHANA_WEB:
      return 'GHANA WEB';
    case LEAD_SOURCE.GOOGLE_BUSINESS:
      return 'GOOGLE BUSINESS';
    case LEAD_SOURCE.INSTAGRAM:
      return 'INSTAGRAM';
    case LEAD_SOURCE.JOY_FM:
      return 'JOY FM';
    case LEAD_SOURCE.LINKEDIN:
      return 'LINKEDIN';
    case LEAD_SOURCE.MYJOY_ONLINE:
      return 'MYJOY ONLINE';
    case LEAD_SOURCE.OTHER:
      return 'OTHER';
    case LEAD_SOURCE.TEAM_DIRECT:
      return 'TEAM DIRECT';
    case LEAD_SOURCE.DIRECT_SALES:
      return 'DIRECT SALES';
    case LEAD_SOURCE.TIKTOK:
      return 'TIKTOK';
    case LEAD_SOURCE.BOLT_APP:
      return 'BOLT APP';
    case LEAD_SOURCE.WEBSITES_OR_BLOGS:
      return 'WEBSITES OR BLOGS';
    case LEAD_SOURCE.SOCIAL_MEDIA:
      return 'SOCIAL MEDIA';
    case LEAD_SOURCE.WHATSAPP:
      return 'WHATSAPP';
    case LEAD_SOURCE.YANGO_APP:
      return 'YANGO_APP';
  }
  return leadSource;
};

export const getFormattedLeadSourceFromEnum = (leadSource: LEAD_SOURCE | string): string => {
  switch (leadSource) {
    case 'ASEMPA FM':
      return LEAD_SOURCE.ASEMPA_FM;
    case 'CITI FM ONLINE':
      return LEAD_SOURCE.CITIFM_ONLINE;
    case 'CITI FM':
      return LEAD_SOURCE.CITI_FM;
    case 'CUSTOMER REFERRAL':
      return LEAD_SOURCE.CUSTOMER_REFERRAL;
    case 'FACEBOOK':
      return LEAD_SOURCE.FACEBOOK;
    case 'GHANA WEB':
      return LEAD_SOURCE.GHANA_WEB;
    case 'GOOGLE BUSINESS':
      return LEAD_SOURCE.GOOGLE_BUSINESS;
    case 'INSTAGRAM':
      return LEAD_SOURCE.INSTAGRAM;
    case 'JOY FM':
      return LEAD_SOURCE.JOY_FM;
    case 'LINKEDIN':
      return LEAD_SOURCE.LINKEDIN;
    case 'MYJOY ONLINE':
      return LEAD_SOURCE.MYJOY_ONLINE;
    case 'OTHER':
      return LEAD_SOURCE.OTHER;
    case 'TEAM DIRECT':
      return LEAD_SOURCE.TEAM_DIRECT;
    case 'TIKTOK':
      return LEAD_SOURCE.TIKTOK;
    case 'BOLT APP':
      return LEAD_SOURCE.BOLT_APP;
    case 'DIRECT SALES':
      return LEAD_SOURCE.DIRECT_SALES;
  }
  return leadSource;
};

export function removeUnderscoresAndDashes(inputString: string, addSpace?: boolean) {
  if (!inputString || inputString === null || inputString === undefined) return '-';
  return inputString.replace(/[_-]/g, addSpace ? ' ' : '');
}

export function removeUnderscoresAndCapitalize(str: string): string {
  if (!str || str === null || str === undefined) return '-';
  const words: string[] = str?.split('_');

  const capitalizedWords: string[] = words?.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );

  return capitalizedWords.join(' ');
}

export function replaceSpacesWithUnderscores(inputString: string) {
  if (!inputString || inputString === null || inputString === undefined) return '-';
  return inputString.replace(/[_\s-]/g, '_');
}

export function putDot(number: number) {
  if (!number || number === null || number === undefined) return '-';
  return (number / 100).toFixed(2);
}

export function numberWithCommas(numb: string | number): string {
  if (numb) {
    const str = numb.toString().split('.');
    str[0] = str[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return str.join('.');
  }
  return String(0.0);
}

export function sentenceCase(str: string) {
  let _tmp = str.toLowerCase();
  let result = _tmp
    .replace(/[a-z]/i, function (letter) {
      return letter.toUpperCase();
    })
    .trim();

  return result;
}

export const isThirdPartyProtection = (protectionType: string): boolean => {
  return protectionType === 'Third Party';
};

export const isHailingService = (vehicleUse: string | undefined): boolean => {
  return vehicleUse === 'HAILING_SERVICE';
};

export const isCommercial = (vehicleUse: string | undefined | null): boolean => {
  return (
    vehicleUse?.toLowerCase() === VEHICLE_USAGES?.HAILING_SERVICE?.title?.toLowerCase() ||
    vehicleUse?.toLowerCase() === VEHICLE_USAGES?.TAXI?.title?.toLowerCase() ||
    vehicleUse?.toLowerCase() === VEHICLE_USAGES?.HIRING_CAR?.title?.toLowerCase()
  );
};

export const getFormattedCoverPeriod = (period: COVER_PERIODS | undefined) => {
  switch (period) {
    case 12:
      return 'One Year';

    case 9:
      return 'Nine Months';

    case 6:
      return 'Six Months';

    case 3:
      return 'Three Months';
  }

  return period;
};

export const policyFormDataToUpdatePolicyDTO = (
  data: IPolicyFormData
): Partial<IUpdatePolicyDTO> => {
  return {
    bodyType: getVehicleBodyTypeFromString(data.vehicleType ?? ''),
    cc: data.cc ?? 0,
    chasisNumber: data.chassisNumber ?? '',
    color: data.vehicleColor ?? '',
    emailAddress: data.email,
    firstname: data.firstName ?? '',
    phone: getFormattedPhoneNumber(data.phoneNumber) ?? '',
    otherPhone: getFormattedPhoneNumber(data.otherPhone) ?? '',
    idExpiry: data.idExpiryDate ? moment(data.idExpiryDate).format(SERVER_DATE_FORMAT) : '',
    idNumber: data.idNumber ?? '',
    idType: data.idType,
    lastname: data.lastName ?? '',
    make: data.vehicleMake,
    manufacturedYear: data.manufacturedYear,
    maxSeat: data.numOfPassenger,
    model: data.vehicleModel,
    registrationNumber: data.registrationNumber,
    registrationYear: data.registrationYear,
    dateOfBirth: moment(data.dateOfBirth).format(SERVER_DATE_FORMAT),
    occupation: data.occupation,
    owner: data.registeredOwner,
    vehicleRegion: data.vehicleRegion,
    mainDriver: data.vehicleMainDriver,
    address: data.address,
    employerName: data.employerName,
    employerAddress: data.employerAddress,
    employerPhone: getFormattedPhoneNumber(data.employerPhoneNumber ?? ''),
    monthlyIncome: data.monthlyIncome,
    declinedInsuranceDetails: data.declinedByOtherInsurer,
    previousInsuranceClaims: data.issuedClaimDetails,
    alterationDetails: data.alterationDetails,
    vehicleAltered: data.vehicleAltered,
    goodRepairState: data.goodRepairState,
    repairState: data.repairState,
    driverComplications: data.diseaseOrComplications,
    insurerId: data.insurer,
    refereeName: data.refereeName,
    refereePhone: getFormattedPhoneNumber(data.refereePhone ?? ''),
    refereeYearsKnown: data.refereeYearsKnown,
    refereeRelationship: data.refereeRelationship,
    hasInterestedParties: data.hasInterestedParties,
    interestedParties: data.interestedParties,
    communityId: data.community ? data.community : null,
    startsOn: data.startsOn,
    expiresOn: data.expiresOn,
    hasInstalledAccessories: data.hasInterestedParties,
    installedAccessoriesDetails: data.installedAccessoriesDetails,
    installedAccessoriesValue: data.installedAccessoriesValue,
    natureOfInterest: data.natureOfInterestInVehicle,
    ownerAddress: data.ownerAddress,
    ownerName: data.ownerName,
    ownerPhone: getFormattedPhoneNumber(data.ownerPhone ?? ''),
    vehicleInGoodState: data.goodRepairState,
    hasDeclinedClaims: data.previouslyDeclined,
    hasPreviousClaims: data.previouslyDeclined,
    rideSharePeriod: data.rideSharePeriod,
    liability: data.liability,
    liabilityDebt: data.liabilityDebt,
    liabilityModeOfPayment: data.liabilityModeOfPayment,
    parkId: data.parkId
  };
};

export const claimFormDataToUpdateClaimDTO = (data: IClaimFormData): Partial<IUpdateClaimDTO> => {
  return {};
};

export function getFormattedPhoneNumber(phone: string | number | null | undefined, withPlus?: boolean): string {
  if (phone === null || phone === undefined || phone === "") return "";

  const phoneStr = typeof phone === "string" ? phone : String(phone);
  const digitsOnly = phoneStr.replace(/\D/g, "");

  if (/^233\d{9}$/.test(digitsOnly) || /^234\d{10}$/.test(digitsOnly)) {
    return digitsOnly;
  }

  if (digitsOnly.startsWith("0") && !withPlus) {
    return digitsOnly.startsWith("080") || digitsOnly.startsWith("081") || digitsOnly.startsWith("090")
      ? `234${digitsOnly.slice(1)}` // Nigeria (234)
      : `233${digitsOnly.slice(1)}`; // Ghana (233)
  }

  if (digitsOnly.startsWith("0") && withPlus) {
    return digitsOnly.startsWith("080") || digitsOnly.startsWith("081") || digitsOnly.startsWith("090")
      ? `+234${digitsOnly.slice(1)}` // Nigeria (234)
      : `+233${digitsOnly.slice(1)}`; // Ghana (233)
  }

  if (phoneStr.startsWith("+233") || phoneStr.startsWith("00233")) {
    return `233${digitsOnly.slice(3)}`;
  }

  if (phoneStr.startsWith("+234") || phoneStr.startsWith("00234")) {
    return `234${digitsOnly.slice(3)}`;
  }

  return digitsOnly.length === 9 ? `233${digitsOnly}` : `234${digitsOnly}`;
}

export function getPhoneNumberFromShortCodeFormat(phone?: string): string {
  if (!phone) return "-";

  const originalPhone = phone?.replace(/\D/g, "");

  const countryCodePattern = /^(\+?(?:233|234))/;
  const match = originalPhone.match(countryCodePattern);

  let formattedPhone;
  if (match) {
    formattedPhone = `0${originalPhone.slice(match[1].length)}`;
  } else {
    formattedPhone = originalPhone.startsWith("0") ? originalPhone : `0${originalPhone}`;
  }

  return formattedPhone === originalPhone ? phone : formattedPhone;
}

export function getVehicleBodyTypeFromString(type: string): VEHICLE_BODY_TYPES | null {
  switch (type?.toLowerCase()) {
    case 'suv':
      return VEHICLE_BODY_TYPES.SUV;

    case 'convertible':
      return VEHICLE_BODY_TYPES.CONVERTIBLE;

    case 'hatchback':
      return VEHICLE_BODY_TYPES.HATCHBACK;

    case 'station wagon':
      return VEHICLE_BODY_TYPES.STATION_WAGON;

    case 'coupe':
      return VEHICLE_BODY_TYPES.COUPE;

    case 'van':
      return VEHICLE_BODY_TYPES.VAN;

    case 'pickup':
      return VEHICLE_BODY_TYPES.PICKUP;

    case 'saloon/sedan':
      return VEHICLE_BODY_TYPES.SALOON_SEDAN;

    case 'mini/small bus':
      return VEHICLE_BODY_TYPES.MINI_SMALL_BUS;

    case 'maxi/big bus':
      return VEHICLE_BODY_TYPES.MAXI_BIG_BUS;

    case 'sport car':
      return VEHICLE_BODY_TYPES.SPORT_CAR;

    case 'articulated truck':
      return VEHICLE_BODY_TYPES.ARTICULATED_TRUCK;

    case 'motorcycle':
      return VEHICLE_BODY_TYPES.MOTOR_BIKE;

    // case 'Art/Tankers':
    //   return VEHICLE_BODY_TYPES.A
  }

  return null;
}

export function getVehicleBodyTypeFromStringV2(
  type: string
): VEHICLE_BODY_TYPES | null {
  if (!type) return null;

  const normalized = type
    .toLowerCase()
    .trim()
    .replace(/[_/]/g, ' ')
    .replace(/\s+/g, ' ');

  return VEHICLE_BODY_TYPE_MAP[normalized] ?? null;
}

export function getListFromEnum(data: any): any[] {
  const list: any[] = [];

  Object.keys(data).forEach((key) => {
    list.push(sentenceCase(data[key]));
  });

  return list;
}

export function getSelectOptionsFromEnum(data: any, addDefault?: boolean): ISelectDropdownItem[] {
  const list: ISelectDropdownItem[] = [];
  if (addDefault) {
    list.push({
      name: 'All',
      value: 'All'
    });
  }

  Object.keys(data).forEach((key) => {
    const stringsArray = data[key].split('_');
    let newName = '';

    stringsArray.forEach((value: string) => {
      newName += ` ${sentenceCase(value)}`;
    });
    list.push({
      name: newName,
      value: data[key]
    });
  });

  return list;
}

export function getListFromRecord(data: any, objectKey: string): any[] {
  const list: any[] = [];

  Object.keys(data).forEach((key) => {
    list.push(sentenceCase(data[key][objectKey]));
  });

  return list;
}

export function getListFromRecordNotSentenceCase(data: any, objectKey: string): any[] {
  const list: any[] = [];

  Object.keys(data).forEach((key) => {
    list.push(data[key][objectKey]);
  });

  return list;
}

export const getSelectableManufacturingYears = () => {
  let thisYear = new Date().getFullYear();
  const yearsList = [new Date().getFullYear()];

  while (thisYear > MINIMUM_ALLOWABLE_MANUFACTURING_YEAR) {
    yearsList.push(thisYear - 1);

    thisYear--;
  }
  return yearsList;
};

export default {
  getNameInitials,
  getColorInitial
};

export const getDiscountRelationsOptionsForTable = (
  relation: DISCOUNT_RELATION_OPTIONS | string
): string => {
  switch (relation) {
    case 'INITIAL_DEPOSIT':
      return DISCOUNT_RELATION_OPTIONS.INITIAL_DEPOSIT;

    case 'MONTHLY_INSTALLMENTS':
      return DISCOUNT_RELATION_OPTIONS.MONTHLY_INSTALLMENTS;

    case 'ONE_TIME':
      return DISCOUNT_RELATION_OPTIONS.ONE_TIME;
  }

  return '';
};

export const getDiscountRelationsOptions = (
  relation: DISCOUNT_RELATION_OPTIONS | string
): string => {
  switch (relation) {
    case DISCOUNT_RELATION_OPTIONS.INITIAL_DEPOSIT:
      return 'INITIAL_DEPOSIT';

    case DISCOUNT_RELATION_OPTIONS.MONTHLY_INSTALLMENTS:
      return 'MONTHLY_INSTALLMENTS';

    case DISCOUNT_RELATION_OPTIONS.ONE_TIME:
      return 'ONE_TIME';
  }

  return '';
};

export const getFormattedPaymentChannel = (channel: PAYMENT_CHANNELS | string): string => {
  switch (channel) {
    case PAYMENT_CHANNELS.MOBILE_MONEY:
      return 'MOBILE MONEY';

    case PAYMENT_CHANNELS.CARD:
      return 'CARD';

    case PAYMENT_CHANNELS.CASH:
      return 'CASH';

    case PAYMENT_CHANNELS.BANK_TRANSFER:
      return 'BANK TRANSFER';

    case PAYMENT_CHANNELS.CHEQUE_DEPOSIT:
      return 'CHEQUE DEPOSIT';

    case PAYMENT_CHANNELS.VOUCHER:
      return 'VOUCHER';

    case PAYMENT_CHANNELS.BANK:
      return 'BANK';
  }

  return '-';
};

export const getValueFromFormattedPaymentChannel = (relation: string): string => {
  switch (relation) {
    case 'MOBILE MONEY':
      return PAYMENT_CHANNELS.MOBILE_MONEY;

    case 'CARD':
      return PAYMENT_CHANNELS.CARD;

    case 'CASH':
      return PAYMENT_CHANNELS.CASH;

    case 'BANK TRANSFER':
      return PAYMENT_CHANNELS.BANK_TRANSFER;

    case 'CHEQUE DEPOSIT':
      return PAYMENT_CHANNELS.CHEQUE_DEPOSIT;

    case 'VOUCHER':
      return PAYMENT_CHANNELS.VOUCHER;

    case 'BANK':
      return PAYMENT_CHANNELS.BANK;
  }
  return '';
};

export const getFormattedAccessRight = (accessRight?: string): string => {
  switch (accessRight) {
    case ACCESS_RIGHTS.OWNER:
      return 'Workspace Owner';
    case ACCESS_RIGHTS.SUPERVISOR:
      return 'Supervisor';
    case ACCESS_RIGHTS.MANAGER:
      return 'Manager';
    case ACCESS_RIGHTS.ADMINISTRATOR:
      return 'Administrator';
    case ACCESS_RIGHTS.AGENT:
      return 'Agent';
  }

  return '';
};

export const getFormattedTeamTypeFromKey = (type?: COMPANY_TYPES): string => {
  switch (type) {
    case COMPANY_TYPES.INTERNAL:
      return 'INTERNAL';
    case COMPANY_TYPES.AGENCY:
      return 'AGENCY';
    case COMPANY_TYPES.RIDE_SHARING:
      return 'RIDE SHARING';
    case COMPANY_TYPES.REPAIR_CENTER:
      return 'REPAIR CENTER';
    case COMPANY_TYPES.AFFILIATE:
      return 'AFFILIATE';
    case COMPANY_TYPES.INSURER:
      return 'INSURER';
    case COMPANY_TYPES.FLEET:
      return 'FLEET';
    case COMPANY_TYPES.FUEL_PROVIDER:
      return 'FUEL PROVIDER';
    case COMPANY_TYPES.BROKER:
      return 'BROKER';
    case COMPANY_TYPES.FINANCIER:
      return 'FINANCIER';
    case COMPANY_TYPES.OTHER:
      return 'OTHER';
  }

  return '';
};

export const getFormattedPartnerTypeFromKey = (type?: PARTNER_TYPES): string => {
  switch (type) {
    case PARTNER_TYPES.RIDE_SHARING:
      return 'RIDE SHARING';
    case PARTNER_TYPES.REPAIR_CENTER:
      return 'AUTO REPAIR CENTER';
    case PARTNER_TYPES.AFFILIATE:
      return 'AFFILIATE';
    case PARTNER_TYPES.INSURER:
      return 'INSURER';
    case PARTNER_TYPES.FLEET:
      return 'FLEET';
    case PARTNER_TYPES.FUEL_PROVIDER:
      return 'FUEL PROVIDER';
    case PARTNER_TYPES.FINANCIER:
      return 'FINANCIER';
    case PARTNER_TYPES.BROKER:
      return 'BROKER';
    case PARTNER_TYPES.AGENCY:
      return 'AGENCY';
    case PARTNER_TYPES.OTHER:
      return 'OTHER';
  }
  return '';
};

export const getAccessRightKeyFromString = (accessRight: string) => {
  switch (accessRight) {
    case 'Owner':
      return ACCESS_RIGHTS.OWNER;
    case 'Manager':
      return ACCESS_RIGHTS.MANAGER;
    case 'Supervisor':
      return ACCESS_RIGHTS.SUPERVISOR;
    case 'Administrator':
      return ACCESS_RIGHTS.ADMINISTRATOR;
    case 'Agent':
      return ACCESS_RIGHTS.AGENT;
  }

  return '';
};

export function getImageExtension(imageFile: any) {
  if (imageFile) {
    let imageName = imageFile.name;
    let dotIndex = imageName.lastIndexOf('.');
    return imageName.substring(dotIndex + 1);
  }
  return 'jpeg';
}

export function blobToFile(compressedFile: any, originalFile: any) {
  return new File([compressedFile], originalFile.name);
}

export const getConvertedAmountFromPesewas = (value: number, numOfDecimalPlaces = 2): string => {
  return value ? (value / 100).toFixed(numOfDecimalPlaces) : '0.00';
};

export const getMarkettingFunnelFromGeneralMetrics = (
  metrics: IGeneralMetrics
): IMarketingFunnel => {
  return {
    allTraffic: metrics.traffic_activity ?? 0,
    allLeads: metrics.leads,
    qualifiedLeads: metrics.leads_status?.cold,
    closedSales: metrics.leads_status?.closed,
    paidSales: metrics.leads_status?.paid
  };
};

export function getPaystackKey() {
  return process.env.REACT_APP_PAYSTACK_KEY;
}

export const isDevelopment = () => {
  return process.env.REACT_APP_ENVIRONMENT === ENVIRONMENTS.DEVELOPMENT;
};

export const isTest = () => {
  return process.env.REACT_APP_ENVIRONMENT === ENVIRONMENTS.TEST;
};

export const isDev = () => {
  return process.env.REACT_APP_ENVIRONMENT === ENVIRONMENTS.DEV;
};

export const isProduction = () => {
  return process.env.REACT_APP_ENVIRONMENT === ENVIRONMENTS.PRODUCTION;
};

export const getEnvironment = () => {
  return process.env.REACT_APP_ENVIRONMENT;
};

export function getFunnelStepGrowth(num: number, denum: number): number {
  if (denum <= 0 && num === 0) {
    return 0;
  }

  if (denum <= 0 && num > 0) {
    return 100;
  }
  const result = (num / denum) * 100;
  return typeof result === 'number' && result >= 0 ? Number(String(result.toFixed(1))) : 0;
}

export function getChangeGrowth(currentValue: number, prevValue: number): number {
  const result = ((currentValue - prevValue) / currentValue) * 100;
  return typeof result === 'number' && result >= 0 ? Number(String(result.toFixed(1))) : 0;
}

export const getIdPlaceholder = (idType: string | undefined, countryId: number): string => {
  let placeholder = '';
  const normalizedIdType = idType?.toLowerCase();
  const placeholderByCountryId = () => {
    if (countryId == 1) placeholder = 'Eg: GHA-XXXXXXXXX-X';

    if (countryId == 2) placeholder = 'Eg: XXXXXXXXXXX';
  }

  switch (normalizedIdType) {
    case 'passport':
      placeholder = 'Eg: GXXXXXXX';
      break;
    case "voter's id":
      placeholder = 'Eg: XXXXXXXXXX';
      break;
    case "driver's lincense":
      placeholder = 'Eg: ABC-XXXXXXXX-XXXXX';
      break;

    case "tin":
      placeholder = 'Enter TIN number';
      break;

    case 'national_id':
      placeholderByCountryId()
      break;

    default:
      placeholder = 'Eg: GHA-XXXXXXXXX-X';
      break;
  }

  return placeholder;
};

export const getIdLabel = (idType: string): string => {
  switch (idType?.toLowerCase()) {
    case 'tin':
      return 'TIN Number';

    default:
      return 'ID Number'
  }
}

export const IntlPhoneInputValidation = {
  phone: Yup.string()
    .when([], (schema: any) => {
      return schema
        .test({
          test: (value: any) => {
            if (value && value.startsWith('+233')) {
              return value.length === 13;
            }
            return true;
          },
          message: 'Phone number must be 10 digits long'
        })
        .required('Phone number is required')
        .matches(/^\+\d+$/, 'Must be only digits')
        .min(7, 'Phone number cannot be less than 7 digits')
        .max(16, 'Phone number cannot be more than 16 digits');
    })
    .trim()
};

export const PhoneInputValidation = {
  phone: Yup.string()
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(10, 'Must be exactly 10 digits')
    .max(10, 'Must be exactly 10 digits')
    .trim()
    .required('Phone number is required.')
};

export const policyStartEndDateValidation = () => {
  const schema = Yup.object().shape({
    startDate: Yup.string().trim().required("Start date is required").nullable()
      .test('is-valid-range', 'Start date must be before end date', function (startDate) {
        const { endDate } = this.parent;
        if (!startDate || !endDate) return true;
        return new Date(startDate) < new Date(endDate);
      }),
    endDate: Yup.string().trim().required("End date is required").nullable()
      .test('is-within-year', 'Policy duration cannot exceed 1 year', function (endDate) {
        const { startDate } = this.parent;
        if (!startDate || !endDate) return true;
        const start = new Date(startDate);
        const end = new Date(endDate);
        const oneYearFromStart = new Date(start);
        oneYearFromStart.setFullYear(start.getFullYear() + 1);
        return end <= oneYearFromStart;
      }),
  })

  return schema;
}

export const getYoutubeVideoIdFromUrl = (youtubeUrl: string): string => {
  const videoURL = youtubeUrl?.split('/');
  let videoId = '';
  videoURL.map((part: any, index: any) => {
    if (part.includes('watch')) {
      videoId = part.split('=')[1];
    } else {
      if (index === 3) {
        videoId = part;
      }
    }
  });

  return videoId;
};

export function capitalizeSentence(sentence: string) {
  // Split the sentence into words using both space and underscore as separators
  const words = sentence?.split(/[ _]/);

  // Capitalize the first letter of each word and join them back into a sentence
  const capitalizedWords = words?.map((word: string) => {
    if (word.length > 0) {
      return word[0].toUpperCase() + word.slice(1).toLowerCase();
    }
    return '';
  });

  // Join the capitalized words into a sentence
  const capitalizedSentence = capitalizedWords.join(' ');

  return capitalizedSentence;
}

export const getAccessRightsList = (data: any) => {
  const list: ISelectDropdownItem[] = [];

  SELECTABLE_ACCESS_RIGHTS.forEach((key) => {
    list.push({
      name: capitalizeSentence(data[key]?.split('_')?.join(' ')),
      value: key
    });
  });

  return list;
};

// export const getInsurerUserTypesList = (data: any) => {
//   const list: ISelectDropdownItem[] = [];

//   Object.keys(ACCESS_RIGHTS)
//     ?.filter((key) => key.toLowerCase().includes('insurer'))
//     .forEach((key) => {
//       list.push({
//         name: capitalizeSentence(data[key]?.split('_')?.join(' ')),
//         value: key
//       });
//     });

//   return list.filter(
//     (listItem: ISelectDropdownItem) => listItem.value !== ACCESS_RIGHTS.INSURER_ADMIN
//   );
// };

export function getClientWebsiteUrl(): string {
  return process.env.REACT_APP_CLIENT_WEBSITE ?? '';
}

export const getPaymentDataFromPolicy = (
  policy: Policy | HomePolicy,
  phone?: string,
  network?: string
): IPolicyPaymentData => {
  return {
    accountNumber: phone ?? policy?.applicant?.phoneNumber ?? '',
    initialAmount: policy.quote?.paymentPlan?.initialDeposit ?? 0,
    network: network ?? '',
    policyNumber: policy.ref,
    recurringAmount: policy.quote?.paymentPlan?.monthlyInstallment ?? 0,
    discountCode: '',
    isFullPayment: policy.isFullPayment(),
    isAutoPolicy: policy.isAutoPolicy(),
    phone: phone ?? policy?.applicant?.phoneNumber ?? ''
  };
};

export const getBooleanSelectDropdownList = () => {
  return [
    {
      name: 'No',
      value: 'no'
    },
    {
      name: 'Yes',
      value: 'yes'
    }
  ];
};

export const getPaystackConfig = (policy?: Policy | HomePolicy): PaystackProps => {
  const amount = policy?.isFullPayment()
    ? (policy?.quote?.paymentPlan?.outRightPremium ?? 0).toFixed(2)
    : (policy?.quote?.paymentPlan?.initialDeposit ?? 0).toFixed(2);

  return {
    reference: new Date().getTime().toString(),
    email: 'support@brolly.africa',
    amount: policy ? Number(amount) * 100 : 0,
    currency: 'GHS',
    publicKey: getPaystackKey() ?? '',
    metadata: {
      policyNumber: policy?.ref,
      custom_fields: [
        {
          display_name: 'Policy Number',
          variable_name: 'policyNumber',
          value: policy?.ref
        }
      ]
    }
  };
};

export async function copyTextToClipboard(text: string) {
  if ('clipboard' in navigator) {
    return await navigator.clipboard.writeText(text);
  } else {
    return document.execCommand('copy', true, text);
  }
}

export const copyToClipboard = async (text: string) => {
  await navigator.clipboard.writeText(text);
};

export function hasSuccessfulPayment(payments: any) {
  for (let i = 0; i < payments.length; i++) {
    for (let key in payments[i]) {
      if (key === 'paymentStatus' && payments[i][key] === 'SUCCESS') {
        return true;
      }
    }
  }
  return false;
}

export function getQuarter(date = new Date()) {
  return Math.floor(date.getMonth() / 3 + 1);
}

export function getPathAndParamsStringFromUrl(url: string) {
  try {
    const parsedUrl = new URL(url);
    const path = parsedUrl.pathname;
    const paramsString = parsedUrl.searchParams.toString();
    return path + (paramsString ? '?' + paramsString : '');
  } catch (error) {
    // console.error('Error parsing URL:', error);
    return null;
  }
}

export const getFormattedSalesStatusFromKey = (status: FUEL_SALES_STATUS): string => {
  switch (status) {
    case FUEL_SALES_STATUS.NEW:
      return 'NEW';

    case FUEL_SALES_STATUS.FAILED:
      return 'FAILED';

    case FUEL_SALES_STATUS.CLOSED:
      return 'CLOSED';

    case FUEL_SALES_STATUS.PENDING_CLOSE:
      return 'PENDING CLOSE';

    case FUEL_SALES_STATUS.PENDING_SETTLEMENT:
      return 'PENDING SETTLEMENT';

    case FUEL_SALES_STATUS.PAID:
      return 'PAID';

    case FUEL_SALES_STATUS.PENDING_PAYMENT:
      return 'PENDING PAYMENT';

    case FUEL_SALES_STATUS.COMPLETED:
      return 'COMPLETED';
  }
  return '';
};

export const getStatusColor = (status: string) => {
  let textColor = '';
  switch (status) {
    case FUEL_SALES_STATUS.COMPLETED:
      textColor = 'text-blue-300';
      break;
    case FUEL_SALES_STATUS.PENDING_CLOSE:
      textColor = 'text-orange-400';
      break;

    case FUEL_SALES_STATUS.PENDING_PAYMENT:
      textColor = 'text-[#FFBF00]';
      break;

    case FUEL_SALES_STATUS.NEW:
      textColor = 'text-red-500';
      break;

    case FUEL_SALES_STATUS.FAILED:
      textColor = 'text-red-500';
      break;

    case FUEL_SALES_STATUS.PAID:
      textColor = 'text-green-500';
      break;

    case FUEL_SALES_STATUS.CLOSED:
      textColor = 'text-blue-500';
      break;

    default:
      textColor = 'text-gray-800';
  }

  return textColor;
};

export const getMediaType = (url: string) => {
  const extension = url.split('.').pop();
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
  const videoExtensions = ['mp4', 'webm', 'ogg'];

  if (extension && imageExtensions.includes(extension)) {
    return 'image';
  } else if (extension && videoExtensions.includes(extension)) {
    return 'video';
  } else {
    return 'unknown';
  }
};

export const onRemoveFilter = (
  filter: ITableFilterItem,
  index: number,
  tableFilters: ITableFilterItem[],
  setTableFilters: (values: any) => void,
  setSearchParams?: (queriesList: Record<string, string>) => void,
  queriesList?: Record<string, string>
) => {
  if (!tableFilters) return;

  const _tableFilters: ITableFilterItem[] = [...tableFilters];

  let thisFilter = { ...tableFilters[index] };
  thisFilter = { ..._tableFilters[index], value: '', value2: '' };

  _tableFilters[index] = thisFilter;

  setTableFilters([..._tableFilters]);

  if (queriesList) {
    updateQueriesList?.(_tableFilters, queriesList);
    setSearchParams?.(queriesList);
  }
};

export const getNumberOfWordsFromString = (text: string, length: number) => {
  const words = text.split(' ');
  const truncatedWords = words.slice(0, length).join(' ');
  return text.length < length ? `${truncatedWords}...` : truncatedWords;
};

export const employeeIncomeBand = (selectedCountryCurrency: string, countryId: number) => {
  const EMPLOYEE_INCOME_BAND = [
    `${selectedCountryCurrency}1,000.00 - ${selectedCountryCurrency}5,000.00`,
    `${selectedCountryCurrency}5,001.00 - ${selectedCountryCurrency}10,000.00`,
    `${selectedCountryCurrency}10,001.00 - ${selectedCountryCurrency}20,000.00`,
    `${selectedCountryCurrency}20,001.00 +`
  ]

  const EMPLOYEE_INCOME_BAND_NAIRA = [
    `${selectedCountryCurrency}50,000.00 - ${selectedCountryCurrency}100,000.00`,
    `${selectedCountryCurrency}100,001.00 - ${selectedCountryCurrency}200,000.00`,
    `${selectedCountryCurrency}200,001.00 - ${selectedCountryCurrency}300,000.00`,
    `${selectedCountryCurrency}300,001.00 +`
  ]
  if (countryId == 1) {
    return EMPLOYEE_INCOME_BAND
  }

  if (countryId == 2) {
    return EMPLOYEE_INCOME_BAND_NAIRA
  }

}

export const registrationNumberPlaceholder = (selectedCountry: string, policy?: Policy) => {

  let placeholder = ''
  switch (selectedCountry) {
    case "GH":
      return placeholder = `Eg.${policy ? policy?.isMotorPolicy() ? "M-24-GE2460" : "GT 2440" : "GT 2440 -20"} `
      break;
    case "NG":
      return placeholder = 'Eg.ABC 123DE'
      break;
    default:
      break;
  }

}

export const getIdValidationPattern = (idType: string, countryCode: string): RegExp => {
  const validationPatterns: { [key: string]: { [key: string]: RegExp } } = {
    GH: {
      'Ghana Card': /^[A-Z]{3}-\d{9}-\d$/,
      'Voter ID': /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/,
      'Passport': /^G[A-Z0-9]{8}$/,
      'Driver License': /^[A-Z]{2}\d{6}$/,
      'SSNIT': /^\d{9}[A-Z]$/,
      'TIN': /^[A-Z]{3}-\\d{8,9}-[A-Z0-9]$/,
    },
    NG: {
      'National ID': /^\d{11}$/,
      'Voter Card': /^[A-Z0-9]{12}$/,
      'Driver License': /^[A-Z]{3}\d{5}[A-Z]{2}$/,
      'Passport': /^[A-Z]\d{8}$/,
      'BVN': /^\d{11}$/,
    },
    KE: {
      'National ID': /^\d{8}$/,
      'Passport': /^[A-Z][A-Z0-9]{7}$/,
      'Alien ID': /^[A-Z]{3}\d{7}$/,
      'Military ID': /^KDF\d{6}$/,
    },
    ZA: {
      'ID Number': /^\d{13}$/,
      'Passport': /^[A-Z][A-Z0-9]{7}$/,
      'Traffic Register': /^[A-Z]{2}\d{6}[A-Z]{2}$/,
    },
  };

  return validationPatterns[countryCode]?.[idType] || /^.+$/;
}

export const sortArrayByAlphabets = (array: { label: string; callback: () => void; }[]) => {
  return array.sort((a, b) => a.label.localeCompare(b.label));
}

export const getGroupedSupportedVehiclesFromIVehicles = (
  supportedVehicles: ISupportedVehicles[]
): Record<string, Array<SupportedVehicle>> => {
  let groupedList: Record<string, Array<SupportedVehicle>> = {};

  const list = supportedVehicles?.map(
    (item: ISupportedVehicles) => new SupportedVehicle(item)
  );
  const sortedList = sortBy(list, "make");
  groupedList = groupBy(sortedList, "make");
  return groupedList;
};

export function predictSupportedVehicles(obj: Record<string, Array<SupportedVehicle>> = {}) {
  const resultsMap: Record<string, Array<ISelectMenuItemValue>> = {};

  map(obj, (vehiclesList, key) => {
    const selectOption: Array<ISelectMenuItemValue> = [];
    for (let i = 0; i < vehiclesList.length; i++) {
      // Checking Existence
      if (!selectOption.find((item) => item.name === vehiclesList[i]?.getDisplayName())) {
        selectOption.push({
          ...vehicleModelToSelectedMenuItem(vehiclesList[i]),
          id: key,
        });
      }
    }

    resultsMap[key] = sortBy(selectOption, "name");
  });
  return resultsMap;
}

export function vehicleModelToSelectedMenuItem(
  vehicle: SupportedVehicle
): ISelectMenuItemValue {
  const menuItem: ISelectMenuItemValue = {
    name: vehicle?.getDisplayName() ?? "",
    value: vehicle?.getDetailedName() ?? "",
    avatar: vehicle?.avatar ?? undefined,
    meta: {
      min: vehicle?.miniSeat ?? undefined,
      max: vehicle?.maxSeat ?? undefined,
      cc: vehicle?.cc ?? undefined,
      detailedName: vehicle?.getDetailedName() ?? "",
    },
  };

  return menuItem;
}

export function getPhonePlaceholder(countryCode?: string): string {
  const placeholders: { [key: string]: string } = {
    GH: '024 123 4567',
    NG: '0803 123 4567',
    KE: '0712 345678',
    ZA: '072 123 4567',
    TZ: '0744 123456',
    UG: '0772 123456',
    RW: '0788 123456',
  };

  return countryCode ? placeholders[countryCode] || '0XX XXX XXXX' : '0XX XXX XXXX';
}

export const previewDocument = (documentUrl: string) => {
  window.open(documentUrl, '_blank');
};


export const getNationalityName = (country: string) => {
  const normalizedCountry = country.toLowerCase();
  switch (normalizedCountry) {
    case 'gh':
    case 'ghana':
      return 'Ghanaian';
    case 'ng':
    case 'nigeria':
      return 'Nigerian';
    case 'ke':
    case 'kenya':
      return 'Kenyan';
    case 'za':
    case 'south africa':
      return 'South African';
    case 'tz':
    case 'tanzania':
      return 'Tanzanian';
    case 'ug':
    case 'uganda':
      return 'Ugandan';
    case 'rw':
    case 'rwanda':
      return 'Rwandan';
    default:
      return 'Unknown';
  }
}

export const phoneValidationSchema = () => {
  const countryPatterns: Record<string, { regex: RegExp; message: string }> = {
    GH: {
      regex: /^(?:\+?233|0)(?:19|20|23|24|25|26|27|28|29|30|31|33|39|40|41|42|43|44|45|46|47|48|49|50|52|53|54|55|56|57|58|59)[0-9]{7}$/,
      message: "Invalid Ghanaian phone number",
    },
    NG: {
      regex: /^(?:\+?234|0)(?:70|80|81|90|91)[0-9]{8}$/,
      message: "Invalid Nigerian phone number",
    },
  };

  const allowedCountries = ["GH", "NG"];

  return Yup.string()
    .required("Phone number is required")
    .test("valid-phone", "Invalid phone number", function (value) {
      if (!value) return false;

      const match = allowedCountries.find((country) =>
        countryPatterns[country].regex.test(value)
      );

      if (match) return true;

      // Default to showing Nigeria error if it starts with +234 or 0 followed by 7–9
      if (/^(\+?234|0)(7|8|9)/.test(value)) {
        return this.createError({ message: countryPatterns.NG.message });
      }

      // Default to Ghana if it starts with +233 or 0 followed by 2 or 5
      if (/^(\+?233|0)(2|3|5)/.test(value)) {
        return this.createError({ message: countryPatterns.GH.message });
      }

      return this.createError({ message: "Please enter a valid phone number" });
    })
    .nullable();
};

export const idValidationSchema = (expiryDateKey?: 'idExpiryDate' | 'incorporationDate' | 'idExpiry', required = true) => {

  return Yup.object().shape({
    idType: Yup.string().required('ID type is required').nullable(),

    idNumber: Yup.string().when('idType', {
      is: (idType: string | undefined) => !!idType,
      then: (schema) =>
        schema.test(
          'id-validation',
          function (
            this: Yup.TestContext,
            value?: string
          ) {
            const idType = this.parent?.idType;

            if (!value) {
              return this.createError({
                message: `${idType === 'TIN' ? 'TIN' : 'ID'} number is required`,
              });
            }

            if (!idType) return true;

            const patterns = [
              getIdValidationPattern(idType, 'GH'),
              getIdValidationPattern(idType, 'NG'),
            ];

            if (!patterns.some((p) => p.test(value))) {
              return this.createError({
                message: 'Invalid ID format',
              });
            }

            return true;
          }
        ),
      otherwise: (schema) => schema.nullable(),
    }),

    [expiryDateKey ?? 'idExpiryDate']:
      expiryDateKey === 'incorporationDate'
        ? Yup.date()
          .required('Incorporation date is required')
          .nullable()
        : required
          ? Yup.date()
            .required('ID expiry date is required')
            .min(new Date(), 'ID must not be expired')
            .nullable()
          : Yup.string().nullable(),
  });
};

export const getCountryIsoFromName = (countryName: string): string => {
  switch (countryName) {
    case 'Ghana':
      return 'GH'

    case 'Nigeria':
      return 'NG'
    default:
      return '';
  }
}

export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

export const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  ghana: 'GHS',
  gh: 'GHS',
  nigeria: 'NGN',
  ng: 'NGN',
  kenya: 'KES',
  ke: 'KES',
  'south africa': 'ZAR',
  za: 'ZAR',
  uganda: 'UGX',
  ug: 'UGX',
  rwanda: 'RWF',
  rw: 'RWF',
};

export const getCurrencySymbolFromCountryIso = (countryIso: string): string => {
  return COUNTRY_CURRENCY_MAP[countryIso.toLowerCase()];
}

export const formatCurrencyAmount = (value?: number | string | null, country?: string, showCurrencySymbol = false): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (numericValue === null || numericValue === undefined || isNaN(numericValue)) {
    return '-';
  }

  const currency = COUNTRY_CURRENCY_MAP[country?.toLowerCase() || ''] || 'GHS';

  const formattedAmount = numberWithCommas(numericValue.toFixed(2));

  return `${showCurrencySymbol ? currency : ''} ${formattedAmount ?? '-'}`;
};

export const formatPercentageValue = (value?: number, toFixedValue?: number, showPercentageSymbol = false): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  return toFixedValue ? `${value?.toFixed(toFixedValue)}${showPercentageSymbol ? '%' : ''}` : `${value?.toFixed(1)}${showPercentageSymbol ? '%' : ''}`;
};

export const formatNumberValue = (value?: number | string) => {
  if (value === null || value === undefined) {
    return '-';
  }

  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (numericValue >= 1_000_000) {
    return `${(numericValue / 1_000_000)?.toFixed(2)}M`;
  }

  if (numericValue >= 1_000) {
    return `${(numericValue / 1_000)?.toFixed(2)}k`;
  }

  if (numericValue < 0) {

    if (numericValue <= -1_000_000) {
      return `-${(Math.abs(numericValue) / 1_000_000)?.toFixed(2)}M`;
    }

    if (numericValue <= -1_000) {
      return `-${(Math.abs(numericValue) / 1_000)?.toFixed(2)}k`;
    }


  }
  return numericValue;
};

export const unformatNumberValue = (value?: number | string) => {
  if (value === null || value === undefined) {
    return '-';
  }

  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (value.toString().toLowerCase().includes('m')) {
    return `${(numericValue * 1_000_000)?.toFixed(0)}`;
  }

  if (value.toString().toLowerCase().includes('k')) {
    return `${(numericValue * 1_000)?.toFixed(0)}`;
  }

  return numericValue;
};

export const getFormattedSentence = (sentence: string): string => {
  const splitted = sentence?.split("_")

  return sentenceCase(splitted?.join(" "));
}

export const getUnformattedSentence = (sentence: string): string => {

  const sentenceWithUnderscore = sentence?.replace(" ", "_")

  return String(sentenceWithUnderscore).toUpperCase();

}

interface IDate {
  date: string | Date | null;
  toServer?: boolean;
  showTime?: boolean
  inputFormat?: string;

}

export const getFormattedDate = ({ date, toServer = false, showTime = false, inputFormat }: IDate): string => {
  if (!date) return '-';

  let parsed;

  let safeDate = date;

  if (typeof date === 'string') {
    // Trim microseconds safely (keep only 3 digits)
    safeDate = date.replace(/\.(\d{3})\d+/, '.$1');
  }

  if (inputFormat && typeof safeDate === 'string') {
    // Use provided format in strict mode
    parsed = moment(safeDate, inputFormat, true);
  } else {
    const knownFormats = [
      moment.ISO_8601,
      'YYYY-MM-DD',
      'DD/MM/YYYY',
      'D/M/YYYY',
      'MM/DD/YYYY',
      'M/D/YYYY',
      'M/D/YY',
      'YYYY/MM/DD',
      'DD-MM-YYYY',
      'D-M-YYYY',
      'YYYYMMDD',
      'DD MMM YYYY',
      'MMM DD, YYYY',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DDTHH:mm:ss.SSS',
      'YYYY-MM-DDTHH:mm:ss.SSSSSS',
      'x',
      'X',
    ];

    parsed = moment(safeDate, knownFormats, true);
  }

  if (!parsed.isValid()) {
    console.warn('Invalid date input:', date);
    return '-';
  }

  const formatStr = toServer
    ? 'YYYY-MM-DD'
    : 'DD-MM-YYYY' + (showTime ? ' HH:mm:ss a' : '');

  return parsed.format(formatStr).toUpperCase();
};

interface IDateFromNumberValue {
  date: number;
}

export const getDateFromNumberValue = ({ date }: IDateFromNumberValue): string => {
  return moment(date).format('YYYY-MM-DD');
}

export const getMonthNameFromDate = (date: Date | string, toUpperCase = true): string => {
  const monthName = moment(date).format('MMMM');
  return toUpperCase ? monthName.toUpperCase() : monthName;
}

export function reduceDataIntoSingleComprehensivePolicyObjectPlusOtherPoliciesForPieCharts(List: IChartItem[], color: string): IChartItem[] {
  const listWithoutComprehensive = List.filter((data: IChartItem) => !String(data.label).includes('Comprehensive'));
  const sumOfComprehensiveCount = List.filter((data: IChartItem) => String(data.label).includes('Comprehensive')).reduce((a: number, b: IChartItem) => a + b.count, 0)
  const sumOfComprehensiveValue = List.filter((data: IChartItem) => String(data.label).includes('Comprehensive')).reduce((a: number, b: IChartItem) => a + b.value, 0)
  const sumOfComprehensivePercentage = List.filter((data: IChartItem) => String(data.label).includes('Comprehensive')).reduce((a: number, b: IChartItem) => a + b.percentage, 0)

  return [
    ...listWithoutComprehensive,
    {
      color: color,
      count: sumOfComprehensiveCount,
      label: "Comprehensive",
      percentage: sumOfComprehensivePercentage,
      value: sumOfComprehensiveValue
    }
  ]

}


export const getDriverLicenseProviderByCountry = (countryIso: string) => {
  if (countryIso == 'GH')
    return 'DVLA'
  if (countryIso == 'NG')
    return 'FRSC'
}

export const getDVLAOrFRSC = (countryIso: string) => {
  if (countryIso == 'GH')
    return NEW_OWNER_YET_TO_CHANGE_AT_DVLA
  if (countryIso == 'NG')
    return NEW_OWNER_YET_TO_CHANGE_AT_FRSC
}

export const getNatureOfInterestListByCountry = (countryIso: string) => {
  if (countryIso == 'GH')
    return [
      NEW_OWNER_YET_TO_CHANGE_AT_DVLA,
      WORK_AND_PAY_DRIVER,
      // HIRE_PURCHASER,
      EMPLOYEE_DRIVER,
      OTHER_AUTHORISED_KEEPER_OR_USER
    ]
  if (countryIso == 'NG')
    return [
      NEW_OWNER_YET_TO_CHANGE_AT_FRSC,
      WORK_AND_PAY_DRIVER,
      // HIRE_PURCHASER,
      EMPLOYEE_DRIVER,
      OTHER_AUTHORISED_KEEPER_OR_USER
    ]
}

export const predictEndDateFromStartDate = (startDate: string | Date) => {
  const dateStart = new Date(startDate);
  const endDate = new Date(dateStart);
  endDate.setDate(dateStart.getDate() + 364);
  return endDate;
}

export const isOfflinePayment = (payment: IPayment) => {
  return payment?.channel == PAYMENT_CHANNELS.CASH || payment?.channel == PAYMENT_CHANNELS.BANK_TRANSFER || payment?.channel == PAYMENT_CHANNELS.BANK || payment?.channel == PAYMENT_CHANNELS.CHEQUE_DEPOSIT
}

export const showExcessBoughtField = (portfolioId: string | number) => portfolioId === PROTECTION_TYPES.COMPREHENSIVE || portfolioId === PROTECTION_TYPES.THIRD_PARTY_FIRE_THEFT || portfolioId === 1 || portfolioId === 2

export const getCoverTypeNameById = (insurerPortfolioId: string | number | undefined, insurerPortfolio?: any[]): string => {
  const id = Number(insurerPortfolioId);
  if (!id || !Array.isArray(insurerPortfolio)) return '';
  const match = insurerPortfolio.find((p: any) => Number(p?.id) === id);
  return String(match?.name ?? '').toLowerCase();
}

export const isComprehensive100Cover = (insurerPortfolioId: string | number | undefined, insurerPortfolio?: any[]): boolean =>
  getCoverTypeNameById(insurerPortfolioId, insurerPortfolio).includes('100%');

export const isComprehensive90Cover = (insurerPortfolioId: string | number | undefined, insurerPortfolio?: any[]): boolean =>
  getCoverTypeNameById(insurerPortfolioId, insurerPortfolio).includes('90%');

export const isComprehensive85Cover = (insurerPortfolioId: string | number | undefined, insurerPortfolio?: any[]): boolean =>
  getCoverTypeNameById(insurerPortfolioId, insurerPortfolio).includes('85%');

export const isPlainComprehensiveCover = (insurerPortfolioId: string | number | undefined, insurerPortfolio?: any[]): boolean => {
  const name = getCoverTypeNameById(insurerPortfolioId, insurerPortfolio);
  return name.includes('comprehensive')
    && !name.includes('85%')
    && !name.includes('90%')
    && !name.includes('100%');
}

export const isFleetPolicy = (policy: Policy | FleetPolicy | undefined): policy is FleetPolicy => {
  return (policy as FleetPolicy)?.fleetNumber !== undefined;
}

export const calculateNumberOfDaysBetweenTwoPeriods = (startDate?: string | Date, endDate?: string | Date) => {
  if (!startDate || !endDate) return 0;
  const dateStart = new Date(startDate);
  const dateEnd = new Date(endDate);

  // Set both dates to the same time to compare just the dates
  dateStart.setHours(0, 0, 0, 0);
  dateEnd.setHours(0, 0, 0, 0);

  // Calculate difference in days
  const timeDiff = dateEnd.getTime() - dateStart.getTime();
  const daysDifference = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

  // Add 1 to include both start and end dates
  const total = daysDifference + 1;
  if (total <= 0) return '';
  return total;
}

export const stepsAndDataPerStep = (numberOfDataPerChart: number, chartData: any[], step: number) => {

  let numberOfSteps;
  let dataPerStep;

  //numberOfSteps with minimum numberOfDataPerChart = 2
  if (chartData?.length > numberOfDataPerChart && chartData?.length <= numberOfDataPerChart * 2) {
    numberOfSteps = 1;
  }
  if (chartData?.length > numberOfDataPerChart * 2 && chartData?.length <= numberOfDataPerChart * 3) {
    numberOfSteps = 2;
  }
  if (chartData?.length > numberOfDataPerChart * 3 && chartData?.length <= numberOfDataPerChart * 4) {
    numberOfSteps = 3;
  }
  if (chartData?.length > numberOfDataPerChart * 4 && chartData?.length <= numberOfDataPerChart * 5) {
    numberOfSteps = 4;
  }
  if (chartData?.length > numberOfDataPerChart * 5 && chartData?.length <= numberOfDataPerChart * 6) {
    numberOfSteps = 5;
  }

  //dataPerStep
  if (step === 0 && chartData?.slice(0, numberOfDataPerChart)) {
    dataPerStep = chartData?.slice(0, numberOfDataPerChart);
  }
  if (step === 1 && chartData?.slice(numberOfDataPerChart, numberOfDataPerChart * 2)) {
    dataPerStep = chartData?.slice(numberOfDataPerChart, numberOfDataPerChart * 2)
  }
  if (step === 2 && chartData?.slice(numberOfDataPerChart * 2, numberOfDataPerChart * 3)) {
    dataPerStep = chartData?.slice(numberOfDataPerChart * 2, numberOfDataPerChart * 3)
  }
  if (step === 3 && chartData?.slice(numberOfDataPerChart * 3, numberOfDataPerChart * 4)) {
    dataPerStep = chartData?.slice(numberOfDataPerChart * 3, numberOfDataPerChart * 4)
  }
  if (step === 4 && chartData?.slice(numberOfDataPerChart * 4, numberOfDataPerChart * 5)) {
    dataPerStep = chartData?.slice(numberOfDataPerChart * 4, numberOfDataPerChart * 5)
  }
  if (step === 5 && chartData?.slice(numberOfDataPerChart * 5, numberOfDataPerChart * 6)) {
    dataPerStep = chartData?.slice(numberOfDataPerChart * 5, numberOfDataPerChart * 6)
  }




  return { numberOfSteps: numberOfSteps || 0, dataPerStep: dataPerStep || [] };
}

export const getTrialEndDaysFromDate = (date: Date | string) => {
  if (!date) return { days: '-', hours: '-', minutes: '-', seconds: '-' };

  const now = new Date().getTime();
  const trialEnd = new Date(date).getTime()
  const difference = trialEnd - now;

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((Math.abs(difference) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((Math.abs(difference) % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((Math.abs(difference) % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
}

export const getInstallmentDescriptionFromKey = (key: string, installment: any) => {
  if (key === 'FULL_PAYMENT') {
    return 'Pay the full amount upfront';
  }
  return `Pay over ${installment?.period} ${key === 'DAILY' ? 'days' : key === 'WEEKLY' ? 'weeks' : 'months'}`;
};

export const getInstallmentLabelFromKey = (key: string) => {
  const labels: { [key: string]: string } = {
    'FULL_PAYMENT': 'Full Payment',
    'THREE_MONTHS': '3 Months',
    'SIX_MONTHS': '6 Months',
    'NINE_MONTHS': '9 Months',
    'TWELVE_MONTHS': '12 Months',
    'DAILY': 'Daily',
    'WEEKLY': 'Weekly',
  };
  return labels[key] || key;
};



//POLICY FLOW MAPPINGS 

export const usageMapToString: Record<number, string> = {
  1: 'Private Use (Individual Owned)',
  2: 'Private Use (Company Owned)',
  3: 'Trade Plate (DV)',
  4: 'Hiring',
  5: 'Taxi',
  6: 'Hailing Services (Uber/Bolt/Yango ...)',
  7: 'Own Goods (Below 3000 cc)',
  8: 'Own Goods (Above 3000 cc)',
  9: 'General Cartage (Below 3000 cc)',
  10: 'General Cartage (Above 3000 cc)',
  11: 'Bus (Private or Commercial Use)',
  12: 'Ambulance/Hearse',
  13: 'Z.802 on site',
  14: 'Z.802 on road',
  15: 'GW1 Class 1',
  16: 'GW1 Class 2',
  17: 'GW1 Class 3',
  18: 'Motorcycle',
  19: 'Art/Tankers',
};

export const insurerMapToString: Record<string, string> = {
  "1": 'Enterprise',
  "2": 'Glico',
  "3": 'Unique Insurance',
  "4": 'Star Assurance',
  "5": 'Sunu',
  "6": 'Hollard Insurance',
  "7": 'Imperial Assurance',
  "8": 'Phoenix Insurance',
  "9": 'Ghana Union Assurance',
  "10": 'SIC Insurance',
  "11": 'Activa Insurance',
  "12": 'Bedrock Insurance',
  "13": 'Best Assurance',
  "14": 'Donewell Insurance',
  "15": 'Coronation Insurance',
  "16": 'Hollard Insurance',
  "17": 'Imperial Assurance',
  "18": 'Loyalty Insurance',
  "19": 'Prime Insurance',
  "20": 'Priority Insurance',
  "21": 'Provident Insurance',
  "22": 'Serene Insurance',
  "23": 'Millenium Insurance',
  "24": 'Nsia Insurance',
  "25": 'Quality Insurance',
};

export const currencyMap: Record<number, string> = {
  1: 'GHS',
  2: 'USD',
  3: 'EUR',
  4: 'NGN',
};

export const bodyTypeMappingFromNumberToString: { [key: string]: string } = {
  '1': 'Saloon/Sedan',
  '2': 'Hatchback',
  '3': 'Coupe',
  '4': 'Convertible',
  '5': 'Maxi/Big Bus',
  '6': 'Mini/Small Bus',
  '7': 'Motorcycle',
  '8': 'Pickup',
  '9': 'Sport Car',
  '10': 'Station Wagon',
  '11': 'SUV',
  '12': 'Van',
  '13': 'Art/Tankers',
  '14': 'Crane',
  '15': 'Excavator',
  '16': 'Forklift',
  '17': 'Hammerhead',
  '18': 'Hilux',
  '19': 'Bulldozer',
};

export const getVehicleBodyTypeNumberFromKey = (bodyType: string) => {
  const bodyTypeMapping: { [key: string]: string } = {
    'SALOON_SEDAN': '1',
    'HATCHBACK': '2',
    'COUPE': '3',
    'CONVERTIBLE': '4',
    'MAXI_BIG_BUS': '5',
    'MINI_SMALL_BUS': '6',
    'MOTOR_BIKE': '7',
    'PICKUP': '8',
    'SPORT_CAR': '9',
    'STATION_WAGON': '10',
    'SUV': '11',
    'VAN': '12',
    'ART_TANKERS': '13',
    'CRANE': '14',
    'EXCAVATOR': '15',
    'FORKLIFT': '16',
    'HAMMERHEAD': '17',
    'HILUX': '18',
    'BULLDOZER': '19',
  };
  return bodyTypeMapping[bodyType] || bodyType;
};

export const generateRandomChassisNumber = (registrationNumber: string): string => {
  const safeReg = (registrationNumber || '')
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/[^A-Z0-9]/g, '');

  const prefix = 'TBA';
  const maxRegLength = 5;
  const regPart = safeReg.slice(0, maxRegLength);
  const randomLength = 17 - prefix.length - regPart.length;

  const chars = 'ABCDEFGHJKLMNPRSTUVWXYZ0123456789';
  let random = '';
  for (let i = 0; i < randomLength; i++) {
    random += chars[Math.floor(Math.random() * chars.length)];
  }

  return `${prefix}${regPart}${random}`;
}

export const generateRandomDateOfBirth = (): Date => {
  // Calculate date range (20-40 years ago from today)
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 40;
  const maxYear = currentYear - 20;

  // Generate a random year between minYear and maxYear
  const randomYear = Math.floor(Math.random() * (maxYear - minYear + 1)) + minYear;

  // Generate random month (0-11)
  const randomMonth = Math.floor(Math.random() * 12);

  // Generate random day (1-28/29/30/31 depending on month and year)
  const daysInMonth = new Date(randomYear, randomMonth + 1, 0).getDate();
  const randomDay = Math.floor(Math.random() * daysInMonth) + 1;

  // Create and return the date
  return new Date(randomYear, randomMonth, randomDay);
}

export const renderFilterValue = (filter: ITableFilterItem) => {
  if (!filter) return null;

  // // Check for boolean values first
  // if (typeof filter.value === 'boolean') {
  //   return filter.value ? 'Yes' : 'No';
  // }

  // Handle simple values (string, number)
  const isSimpleValue = (
    typeof filter.value === 'string' ||
    typeof filter.value === 'number' ||
    typeof filter.value2 === 'string'
  ) && !(filter?.value as any)?.from &&
    !(filter?.value as any)?.to &&
    filter.label;

  if (isSimpleValue) {
    const useValue2Labels = [
      'Insurer',
      'To insurer',
      'Broker',
      'Policy manager',
      'Payment Status',
      'Approved Status',
      'From broker'
    ];

    const shouldUseValue2 = useValue2Labels.includes(String(filter.label));
    const rawValue = shouldUseValue2 ? filter.value2 : filter.value;
    return getFormattedSentence(rawValue);
  }

  // Handle date range
  if (filter?.value?.from) {
    const from = moment(String(filter.value.from)).format('DD-MM-YYYY');
    const to = filter.value.to
      ? ` to ${moment(String(filter.value.to)).format('DD-MM-YYYY')}`
      : '';
    return `${from}${to}`;
  }

  return null;
};

export const getOrUpdateQueriesFromUrl = (page: number, searchParams: URLSearchParams, tableFilters: ITableFilterItem[], queriesList: Record<string, string>, queriesName: string[]) => {

  tableFilters.filter((filter: ITableFilterItem) => filter.value !== '')
    .forEach((filter: ITableFilterItem) => {
      queriesList[filter.name] = String(filter.value);
    });

  queriesName.map((f: string) => {
    if (searchParams.get(f) && f !== 'expiry_date' && f !== 'create_date')
      queriesList[f] = String(searchParams.get(f))
    queriesList['page'] = String(page)
  })

}

export const updateQueriesList = (filters: ITableFilterItem[], queriesList: Record<string, string>) => {

  filters.filter((filter: ITableFilterItem) => filter.value !== '' && filter.name !== 'expiry_date' && filter.name !== 'create_date')
    .forEach((filter: ITableFilterItem) => {
      queriesList[filter.name] = String(filter.value);
    });

}

export const getPolicyIssuanceModeDescriptionFromEnum = (mode: POLICY_ISSUANCE_MODES): string => {
  return POLICY_ISSUANCE_MODES_WITH_DESCRIPTION[mode]?.description || '';
}

export const showNCDFieldOnPolicyJourney = (selectedCoverType?: number, insurerPortfolio?: any) => {
  if (!selectedCoverType || !insurerPortfolio) return false;
  return insurerPortfolio?.filter((item: any) => item.id === Number(selectedCoverType))?.[0]
    ?.name?.toLowerCase() !== 'third party'
}

export const isNumberValue = (value: string | number) => {
  if (typeof value === 'number') return !isNaN(value);
  if (typeof value === 'string') {
    // Allow leading zeros and optional spaces at the end
    return /^0*\d+\s*$/.test(value.trim());
  }
  return false;
}

export const onDownloadFileFromUrl = async (fileUrl: string, customFileName?: string) => {
  try {
    let downloadUrl = fileUrl;
    let fileName = customFileName || 'template.csv';

    // Handle Google Sheets URL - convert to CSV export URL and open in new tab
    if (fileUrl.includes('docs.google.com/spreadsheets')) {
      // Extract spreadsheet ID from Google Sheets URL
      const match = fileUrl.match(/\/d\/([a-zA-Z0-9-_]+)/);
      if (match) {
        const spreadsheetId = match[1];
        downloadUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/export?format=csv&gid=0`;
        // For Google Sheets, open in new tab due to CORS/authentication restrictions
        window.open(downloadUrl, '_blank');
        return;
      }
    }

    // Extract filename from URL if no custom filename provided
    if (!customFileName) {
      const urlFileName = downloadUrl.split('/').pop();
      if (urlFileName && !urlFileName.includes('export')) {
        fileName = urlFileName;
      }
    }

    // Fetch the file
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    // Get the blob
    const blob = await response.blob();

    // Create a temporary anchor element
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    // Trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    // Fallback to opening in new tab if download fails
    window.open(fileUrl, '_blank');
  }
}

// Toast Helper Functions
export const showToast = {
  success: (message: string, description?: string, options?: any) => {
    const content = description ? `${message}: ${description}` : message;
    return toast.success(content, {
      autoClose: 5000,
      ...options
    });
  },

  error: (message: string, description?: string, options?: any) => {
    const content = description ? `${message}: ${description}` : message;
    return toast.error(content, {
      autoClose: 5000,
      ...options
    });
  },

  info: (message: string, description?: string, options?: any) => {
    const content = description ? `${message}: ${description}` : message;
    return toast.info(content, {
      autoClose: 5000,
      ...options
    });
  },

  warning: (message: string, description?: string, options?: any) => {
    const content = description ? `${message}: ${description}` : message;
    return toast.warning(content, {
      autoClose: 5000,
      ...options
    });
  }
};
